import React, {Fragment, useContext} from 'react';
import glamorous from 'glamorous';
import PropTypes from 'prop-types';
import DescriptionCard from '../../../shared/library/cards/description';
import {makeSentence} from '../../../shared/utils/make-sentence';
import ItemList from '../../../shared/library/cards/item-list';
import {ComponentContainer, Spinner, Center} from './shared/styles';
import {toolIntegrations} from '../../../data/tool-profile/queries';
import {compose, Query} from 'react-apollo';
import Circular, {LARGE} from '../../../shared/library/indicators/indeterminate/circular';
import {updateQuery} from '../utils';
import NavAnchor, {NAV_ANCHOR_OFFSET} from '../../tool-profile/components/shared/nav-anchor';
import {flattenEdges} from '../../../shared/utils/graphql';
import {TARMAC} from '../../../shared/style/colors';
import {BASE_TEXT} from '../../../shared/style/typography';
import {MobileContext} from '../../../shared/enhancers/mobile-enhancer';
import SignUp from '../../../shared/library/buttons/sign-up';
import {withAnalyticsPayload} from '../../../shared/enhancers/analytics-enhancer';
import withErrorBoundary from '../../../shared/enhancers/error-boundary';

const SERVICE_INTEGRATIONS = 'service_integrations';

const Container = glamorous.div({
  margin: '25px 0'
});

const Description = glamorous.div({
  color: TARMAC,
  ...BASE_TEXT,
  marginTop: 8,
  lineHeight: 1.69
});

const IntegrationDescription = ({toolName, firstFiveIntegrations, toolIntegrationsCount}) => {
  return (
    <Description>
      {makeSentence(firstFiveIntegrations, true)} are some of the popular tools that integrate with{' '}
      {toolName}. Here&#39;s a list of all <strong>{toolIntegrationsCount}</strong> tools that
      integrate with {toolName}.
    </Description>
  );
};

const Integration = ({tool, currentUser, isPackage}) => {
  const isMobile = useContext(MobileContext);
  return (
    <ComponentContainer>
      {!isPackage && <NavAnchor id="integrations" offset={NAV_ANCHOR_OFFSET} />}
      <Query
        query={toolIntegrations}
        variables={{id: tool.id, first: isMobile ? 8 : 9}}
        notifyOnNetworkStatusChange={true}
      >
        {({loading, data, fetchMore}) => {
          const hasData = data && data.tool;
          if (loading && !hasData)
            return (
              <Spinner>
                <Circular size={LARGE} />
              </Spinner>
            );

          if (hasData) {
            const toolIntegrations = flattenEdges(data.tool.allToolIntegrations);
            const toolIntegrationsCount = data.tool.allToolIntegrations.count;
            const firstFiveIntegrations =
              toolIntegrations &&
              toolIntegrations.slice(0, 5).map(integration => {
                return integration.name;
              });

            const integrationData =
              toolIntegrations &&
              toolIntegrations.map(company => {
                return {
                  name: company.name,
                  imageUrl: company.imageUrl,
                  thumbUrl: company.thumbRetinaUrl,
                  slug: company.slug,
                  path: company.path,
                  id: company.id
                };
              });

            const endCursor = data.tool.allToolIntegrations.pageInfo.endCursor;
            const hasNextPage = data.tool.allToolIntegrations.pageInfo.hasNextPage;

            const onLoadMoreClick = () => {
              fetchMore({
                variables: {
                  after: endCursor,
                  first: isMobile ? 16 : 18
                },
                updateQuery
              });
            };

            return (
              <Fragment>
                <DescriptionCard
                  heading={isPackage ? 'Integrates with' : `${tool.name} Integrations`}
                />
                <Fragment>
                  {toolIntegrations.length >= 5 && (
                    <IntegrationDescription
                      firstFiveIntegrations={firstFiveIntegrations}
                      toolIntegrationsCount={toolIntegrationsCount}
                      toolName={tool.name}
                    />
                  )}
                </Fragment>
                <Container>
                  <ItemList
                    item={integrationData}
                    loading={loading}
                    hasNextPage={currentUser && hasNextPage}
                    notFoundItem="integrations"
                    loadMore={onLoadMoreClick}
                  />
                  <Center>
                    {!currentUser && hasNextPage && (
                      <SignUp text="Sign up to see more" section={SERVICE_INTEGRATIONS} />
                    )}
                  </Center>
                </Container>
              </Fragment>
            );
          }
        }}
      </Query>
    </ComponentContainer>
  );
};

Integration.propTypes = {
  tool: PropTypes.object,
  currentUser: PropTypes.object,
  isPackage: PropTypes.bool
};

IntegrationDescription.propTypes = {
  toolName: PropTypes.string,
  firstFiveIntegrations: PropTypes.array,
  toolIntegrationsCount: PropTypes.number
};

export default compose(
  withErrorBoundary(true),
  withAnalyticsPayload()
)(Integration);
